import React, { useState } from "react";
import { ChromePicker } from "react-color";
import { useApp } from "../../common/contexts/AppContext";
import BaseContainerWrapper from "../../common/components/BaseContainerWrapper";
import useSubscribers from "../../common/hooks/pages/useSubscribers";
import { useMutation, useQuery } from "react-query";
import { usePopup } from "../../common/contexts/PopupContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EmbedModal from "../components/EmbedModal";
import {
  faTimes,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

function StatusEmbed() {
  const { isOwner } = useApp();

  const { saveSettings, getSettings } = useSubscribers();

  const { toast } = usePopup();

  const [selectedPosition, setSelectedPosition] = useState();

  const [colors, setColors] = useState();

  const [layout, setLayout] = useState({
    bottom: "0",
    right: "0",
    left: "240",
    top: "20",
  });

  const [selectedColor, setSelectedColor] = useState(null);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);

  const handlePositionChange = (position) => {
    setSelectedPosition(position);
    switch (position) {
      case "bottomLeft":
        setLayout({
          bottom: "20",
          right: "0",
          left: "10",
          top: "160",
        });
        break;
      case "bottomRight":
        setLayout({
          bottom: "20",
          right: "10",
          left: "240",
          top: "160",
        });
        break;
      case "topRight":
      default:
        setLayout({
          bottom: "0",
          right: "0",
          left: "240",
          top: "20",
        });
        break;
    }
  };

  const handleColorChange = (color, key) => {
    setColors({ ...colors, [key]: color.hex });
  };

  const toggleColorPicker = (colorKey) => {
    setSelectedColor(selectedColor === colorKey ? null : colorKey);
  };

  const closeColorPicker = () => {
    setSelectedColor(null);
  };

  const handleSubmit = () => {
    const payload = { position: selectedPosition, colors: colors };
    saveSettingsMutate(payload);
  };

  const handlePopup = () => {
    setEmbedModalOpen(true);
  };

  const { isError, isLoading, refetch } = useQuery({
    retry: false,
    queryKey: "getSettings",
    queryFn: getSettings,
    onSuccess: (res) => {
      setColors({
        incidentBackground: res?.data?.data?.incidentBackground || "#B35E14",
        incidentTextColor: res?.data?.data?.incidentText || "#FFFFFF",
        maintenanceBackground:
          res?.data?.data?.maintenanceBackground || "#207AB6",
        maintenanceTextColor: res?.data?.data?.maintenanceText || "#FFFFFF",
      });
      handlePositionChange(res?.data?.data?.position || "topRight");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        "Something went wrong while getting settings!";
      toast(message, "error");
    },
  });

  const { mutate: saveSettingsMutate, isLoading: isCreating } = useMutation({
    mutationFn: saveSettings,
    onSuccess: (res) => {
      const { message } = res?.data || {};
      toast(message, "success");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        "Something went wrong while update settings!";
      toast(message, "error");
    },
  });

  return (
    <BaseContainerWrapper>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 text-2xl font-bold pb-8 border-b">
          Status Embed
        </div>
        <div className="col-span-2">
          <p>
            Embedding your status in web apps, sites, portals, or forms will
            increase visibility of your system status and provide an easy way
            for users to access your page.
          </p>
        </div>
      </div>
      <br />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-6">
          <h1 className="text-1xl font-bold">Positioning</h1>
          <label className="statusRadioBtn">
            <input
              type="radio"
              name="position"
              className={`px-2 py-2 transition ease-in-out delay-150 w-half`}
              checked={selectedPosition === "bottomLeft"}
              onChange={() => handlePositionChange("bottomLeft")}
            />
            <span className="ml-2">Bottom left</span>
          </label>
          <label className="statusRadioBtn">
            <input
              type="radio"
              name="position"
              checked={selectedPosition === "bottomRight"}
              className={`px-2 py-2 transition ease-in-out delay-150 w-half`}
              onChange={() => handlePositionChange("bottomRight")}
            />
            <span className="ml-2"> Bottom right</span>
          </label>
          <label className="statusRadioBtn">
            <input
              type="radio"
              name="position"
              className={`px-2 py-2 transition ease-in-out delay-150 w-half`}
              checked={selectedPosition === "topRight"}
              onChange={() => handlePositionChange("topRight")}
            />
            <span className="ml-2"> Top right </span>
          </label>
        </div>
        <div className="col-span-6">
          <div style={{ position: "relative" }}>
            <img
              src="https://dka575ofm4ao0.cloudfront.net/packs/manage/75ab3740f639e314d488293059b74866.svg"
              alt="First image"
            />
            <img
              src="https://dka575ofm4ao0.cloudfront.net/packs/manage/796f952f30088cf8bcbabb7e9cc73222.svg"
              alt="Second image"
              style={{
                position: "absolute",
                bottom: `${layout?.bottom}px`,
                right: `${layout?.right}px`,
                left: `${layout?.left}px`,
                top: `${layout?.top}px`,
              }}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-6">
          <h1 className="text-1xl font-bold">Colors</h1>
          <div className="colors-grid">
            <div className="color-section">
              <label className="color-label statusRadioBtn">
                Incident Background
              </label>
              <input
                type="text"
                className={`px-2 py-2 rounded-md border-2 border-gray-300 outline-none focus:ring-2  transition ease-in-out delay-150 w-half`}
                value={colors?.incidentBackground}
                onFocus={() => toggleColorPicker("incidentBackground")}
                readOnly
              />
              {selectedColor === "incidentBackground" && (
                <ChromePicker
                  color={colors?.incidentBackground}
                  onChange={(color) =>
                    handleColorChange(color, "incidentBackground")
                  }
                  onChangeComplete={closeColorPicker}
                />
              )}
              <label className="color-label statusRadioBtn">
                Incident Text Color
              </label>
              <input
                type="text"
                className={`px-2 py-2 rounded-md border-2 border-gray-300 outline-none focus:ring-2  transition ease-in-out delay-150 w-half`}
                value={colors?.incidentTextColor}
                onFocus={() => toggleColorPicker("incidentTextColor")}
                readOnly
              />
              {selectedColor === "incidentTextColor" && (
                <ChromePicker
                  color={colors?.incidentTextColor}
                  onChange={(color) =>
                    handleColorChange(color, "incidentTextColor")
                  }
                  onChangeComplete={closeColorPicker}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-span-6">
          <br />
          <div
            className="card"
            style={{ backgroundColor: `${colors?.incidentBackground}` }}
          >
            <div
              className="card-content"
              style={{ color: `${colors?.incidentTextColor}` }}
            >
              <button className="card-close-button">
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <h3 className="card-title">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="exclamation-icon icon-text"
                />
                There is a login problem on our apps
              </h3>
              <p className="card-description desc">
                Last updated 24 minutes ago
              </p>
              <p
                className="card-description desc"
                style={{ paddingTop: "10px" }}
              >
                <a className="underline" href="View latest updates">
                  View latest updates
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <br />
      <br />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-6">
          <div className="colors-grid">
            <div className="color-section">
              <label className="color-label statusRadioBtn">
                Maintenance Background
              </label>
              <input
                type="text"
                className={`px-2 py-2 rounded-md border-2 border-gray-300 outline-none focus:ring-2  transition ease-in-out delay-150 w-half`}
                value={colors?.maintenanceBackground}
                onFocus={() => toggleColorPicker("maintenanceBackground")}
                readOnly
              />
              {selectedColor === "maintenanceBackground" && (
                <ChromePicker
                  color={colors?.maintenanceBackground}
                  onChange={(color) =>
                    handleColorChange(color, "maintenanceBackground")
                  }
                  onChangeComplete={closeColorPicker}
                />
              )}
              <label className="color-label statusRadioBtn">
                Maintenance Text Color
              </label>
              <input
                type="text"
                className={`px-2 py-2 rounded-md border-2 border-gray-300 outline-none focus:ring-2  transition ease-in-out delay-150 w-half`}
                value={colors?.maintenanceTextColor}
                onFocus={() => toggleColorPicker("maintenanceTextColor")}
                readOnly
              />
              {selectedColor === "maintenanceTextColor" && (
                <ChromePicker
                  color={colors?.maintenanceTextColor}
                  onChange={(color) =>
                    handleColorChange(color, "maintenanceTextColor")
                  }
                  onChangeComplete={closeColorPicker}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-span-6">
          <br />
          <div
            className="card"
            style={{ backgroundColor: `${colors?.maintenanceBackground}` }}
          >
            <div
              className="card-content"
              style={{ color: `${colors?.maintenanceTextColor}` }}
            >
              <button className="card-close-button">
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <h3 className="card-title">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="exclamation-icon icon-text"
                />
                Maintenance on internal systems this weekend
              </h3>
              <p className="card-description desc">
                29 May 2024 from 08:00 to 10:30 UTC
              </p>
              <p
                className="card-description desc"
                style={{ paddingTop: "10px" }}
              >
                <a className="underline" href="View latest updates">
                  View latest updates
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <br />
      <br />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-6"></div>
        <div className="col-span-6 flex gap-4" style={{ float: "right" }}>
          <button
            btnCls="!w-fit"
            className="rounded-md  fileButton text-white px-4 py-2 nav_link-500 transition ease-in-out duration-300"
            onClick={handlePopup}
          >
            Copy Code
          </button>
          {isOwner ? (
            <button
              btnCls="!w-fit"
              className="rounded-md common_bg text-white px-4 py-2 nav_link-500 transition ease-in-out duration-300"
              onClick={handleSubmit}
            >
              Save
            </button>
          ) : null}
        </div>
      </div>
      <br />
      <br />
      {embedModalOpen && (
        <EmbedModal
          title="Embed Script"
          open={true}
          setOpen={setEmbedModalOpen}
          onClick={() => setEmbedModalOpen(false)}
        />
      )}
    </BaseContainerWrapper>
  );
}

export default StatusEmbed;
